"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, User } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";
import { format } from "date-fns";

interface CustomerWashHistoryDashboardProps {
  data: {
    customerId: string;
    customerName: string;
    dateRange: {
      start: string;
      end: string;
    };
    history: Array<{
      cleanDate: string;
      serviceType: string;
      assignedUnit: string;
      status: string;
      washCount: number;
    }>;
    totalWashes: number;
  };
}

export function CustomerWashHistoryDashboard({
  data,
}: CustomerWashHistoryDashboardProps) {
  const handleExport = () => {
    const exportData = data.history.map((row) => ({
      "Clean Date": format(new Date(row.cleanDate), "dd/MM/yyyy"),
      "Customer Name": data.customerName,
      "Service Type": row.serviceType,
      "Assign (Unit)": row.assignedUnit,
      Status: row.status,
      "No. Wash": row.washCount,
    }));

    // Add totals row
    exportData.push({
      "Clean Date": "TOTAL",
      "Customer Name": data.customerName,
      "Service Type": "-",
      "Assign (Unit)": "-",
      Status: "-",
      "No. Wash": data.totalWashes,
    });

    exportToExcel(
      exportData,
      `Wash_History_${data.customerName.replace(/\s+/g, "_")}`,
      "Wash History"
    );
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
      case "FINISHED":
        return "default";
      case "CANCELLED":
        return "destructive";
      case "IN_PROGRESS":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    <div className="space-y-4">
      {/* Customer Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {data.customerName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground">Customer ID</p>
              <p className="text-lg font-medium">{data.customerId}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Period</p>
              <p className="text-lg font-medium">
                {format(new Date(data.dateRange.start), "dd MMM")} -{" "}
                {format(new Date(data.dateRange.end), "dd MMM yyyy")}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Washes</p>
              <p className="text-3xl font-bold text-blue-600">
                {data.totalWashes}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wash History Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Wash History</CardTitle>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Clean Date</TableHead>
                <TableHead>Service Type</TableHead>
                <TableHead>Assign (Unit)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">No. Wash</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    No wash history found for this customer
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {data.history.map((record, index) => (
                    <TableRow key={`${record.cleanDate}-${index}`}>
                      <TableCell>
                        {format(new Date(record.cleanDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell>{record.serviceType}</TableCell>
                      <TableCell>{record.assignedUnit}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(record.status)}>
                          {record.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {record.washCount}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell colSpan={4}>TOTAL</TableCell>
                    <TableCell className="text-right">
                      {data.totalWashes}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
